import { useState, useEffect, useCallback } from 'preact/hooks'
import { useSearch } from 'wouter-preact'
import { Seite } from '../app.jsx'
import { api } from '../lib/api.js'
import { faelligText, heuteIso } from '../lib/format.js'
import { useBeiRueckkehr } from '../lib/laden.js'
import { Laden, Fehler, Leer } from '../components/Zustand.jsx'

/**
 * Kartenverwaltung: Liste, Suche, Verschieben, Reihenfolge, Waisen.
 *
 * Dichte hier „komfortabel" statt großzügig (DESIGN.md, Abschnitt Abstände) —
 * das ist kein Lernbildschirm, hier darf Information stehen.
 *
 * Reihenfolge wird über zwei Pfeilknöpfe je Zeile geändert, nicht über
 * HTML5-Drag: die App ist iPhone-first, und Drag-and-Drop trägt auf
 * iOS-Safari nicht zuverlässig.
 */

/**
 * Bildmarken-Karten führen in den Bildeditor, nicht in den Texteditor: sie
 * gehören zu einem Satz, und einzeln bearbeitet liefen die Maskenlisten der
 * Geschwister auseinander.
 */
function bearbeitenPfad(karte) {
  if (karte.card_type === 'io' && karte.image) {
    return `/karten/bild?image=${encodeURIComponent(karte.image)}&folder=${encodeURIComponent(karte.folder)}`
  }
  return `/karten/bearbeiten?id=${encodeURIComponent(karte.id)}`
}

/**
 * Eine Karte gilt als neu, wenn sie noch nie bewertet wurde. Das Zurücksetzen
 * hätte dann nichts zu tun, deshalb steht der Knopf dort gar nicht erst — die
 * Zeile sagt in der Meta-Zeile ohnehin schon „neu".
 */
function istNeu(karte) {
  return !karte.due && !karte.repetitions
}

/**
 * Eine Einstellung des gewählten Themas als Zeile mit Kippschalter.
 *
 * Zeile statt Knopf mit Erklärsatz: auf dem Telefon standen sonst zwei Absätze
 * Fließtext zwischen Filter und Kartenliste. Der Schalter zeigt den Zustand
 * schon selbst, deshalb steht der Hinweis nur im Ein-Zustand — dort sagt er,
 * was gerade gilt, statt zu erklären, was passieren würde.
 */
function Themenschalter({ titel, an, aufAendern, hinweis }) {
  return (
    <div class="themenschalter">
      <label class="themenschalter__kopf">
        <span class="themenschalter__titel">{titel}</span>
        <input
          type="checkbox"
          role="switch"
          class="themenschalter__kippe"
          checked={an}
          onChange={(e) => aufAendern(e.currentTarget.checked)}
        />
      </label>
      {an ? <p class="themenschalter__hinweis meta">{hinweis}</p> : null}
    </div>
  )
}

/**
 * Eine Zeile der Liste. Die Aktionen stehen eingeklappt hinter ⋯: offen
 * waren es fünf Bedienelemente in zwei Zeilen je Karte, und auf dem Telefon
 * war die Liste mehr Knopf als Karte.
 */
function Kartenzeile({ karte, heute, ordner, offen, aufUmschalten, aufVerschieben, aufLoeschen, aufZuruecksetzen, aufSchieben, istErste, istLetzte, nachfrage, aufNachfrage, aufAbbrechen }) {
  return (
    <li class="kartenzeile">
      <div class="kartenzeile__text">
        <a class="kartenzeile__vorderseite" href={bearbeitenPfad(karte)}>
          {karte.front_preview || karte.title}
        </a>
        <p class="kartenzeile__meta meta">
          {karte.card_type === 'mc' ? <span class="chip">MC</span> : null}
          {karte.card_type === 'io' ? <span class="chip">Bild</span> : null}
          {karte.parse_error ? (
            <span class="kartenzeile__fehler">Nicht lesbar: {karte.parse_error}</span>
          ) : (
            <>
              {faelligText(karte.due, heute)}
              {karte.repetitions ? ` · ${karte.repetitions} Wdh.` : null}
            </>
          )}
        </p>
      </div>

      <button
        type="button"
        class="kartenzeile__schalter"
        aria-label={`Aktionen für ${karte.title}`}
        aria-expanded={offen ? 'true' : 'false'}
        onClick={() => aufUmschalten(karte.id)}
      >
        ⋯
      </button>

      {offen ? (
      <div class="kartenzeile__aktionen">
        <button
          type="button"
          class="knopf knopf--still knopf--klein"
          data-aktion="hoch"
          aria-label="Nach oben"
          disabled={istErste}
          onClick={() => aufSchieben(karte.id, -1)}
        >
          ↑
        </button>
        <button
          type="button"
          class="knopf knopf--still knopf--klein"
          data-aktion="runter"
          aria-label="Nach unten"
          disabled={istLetzte}
          onClick={() => aufSchieben(karte.id, 1)}
        >
          ↓
        </button>

        <label class="kartenzeile__ordner">
          <span class="visuell-versteckt">Thema von {karte.title}</span>
          <select
            class="feld__eingabe"
            value={karte.folder}
            onChange={(e) => aufVerschieben(karte.id, e.currentTarget.value)}
          >
            {ordner.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </label>

        {nachfrage === 'zuruecksetzen' ? (
          <>
            <button
              type="button"
              class="knopf knopf--still knopf--klein kartenzeile__ernst"
              onClick={() => aufZuruecksetzen(karte.id)}
            >
              Wirklich zurücksetzen
            </button>
            <button type="button" class="knopf knopf--still knopf--klein" onClick={aufAbbrechen}>
              Abbrechen
            </button>
          </>
        ) : nachfrage === 'loeschen' ? (
          <>
            <button
              type="button"
              class="knopf knopf--still knopf--klein kartenzeile__loeschen"
              onClick={() => aufLoeschen(karte.id)}
            >
              Wirklich löschen
            </button>
            <button type="button" class="knopf knopf--still knopf--klein" onClick={aufAbbrechen}>
              Abbrechen
            </button>
          </>
        ) : (
          <>
            {istNeu(karte) ? null : (
              <button
                type="button"
                class="knopf knopf--still knopf--klein"
                data-aktion="zuruecksetzen"
                aria-label={`Lernstand von ${karte.title} zurücksetzen`}
                onClick={() => aufNachfrage(karte.id, 'zuruecksetzen')}
              >
                Zurücksetzen
              </button>
            )}
            <button
              type="button"
              class="knopf knopf--still knopf--klein"
              data-aktion="loeschen"
              aria-label={`${karte.title} löschen`}
              onClick={() => aufNachfrage(karte.id, 'loeschen')}
            >
              Löschen
            </button>
          </>
        )}
      </div>
      ) : null}
    </li>
  )
}

export function Karten() {
  const suche = useSearch()
  const prefixAusAdresse = new URLSearchParams(suche).get('prefix') || ''

  const [prefix, setPrefix] = useState(prefixAusAdresse)
  const [frage, setFrage] = useState('')
  const [daten, setDaten] = useState(null)
  const [laedt, setLaedt] = useState(true)
  const [fehler, setFehler] = useState(null)
  const [meldung, setMeldung] = useState(null)
  // { id, aktion } — beide zerstörenden Aktionen fragen zweistufig nach, und es
  // darf immer nur eine Zeile offen stehen.
  const [nachfrage, setNachfrage] = useState(null)
  // Die aufgeklappte Zeile. Immer nur eine — eine offene Nachfrage gehört zu
  // ihr und geht mit, wenn eine andere aufklappt.
  const [offen, setOffen] = useState(null)
  const umschalten = useCallback((id) => {
    setNachfrage(null)
    setOffen((jetzt) => (jetzt === id ? null : id))
  }, [])

  const laden = useCallback(() => {
    setLaedt(true)
    return api
      .get('/api/cards', { prefix, q: frage })
      .then((antwort) => {
        setDaten(antwort)
        setLaedt(false)
        return antwort
      })
      .catch((f) => {
        setFehler(f)
        setLaedt(false)
      })
  }, [prefix, frage])

  useEffect(() => {
    laden()
  }, [laden])

  // Auch hier steht eine Fälligkeitszahl — sie muss stimmen, wenn die App aus
  // dem Hintergrund zurückkommt und zwischendurch gelernt wurde.
  useBeiRueckkehr(laden)

  const verschieben = useCallback(
    (id, ordner) => {
      setMeldung(null)
      api
        .post('/api/cards/move', { id, folder: ordner })
        .then(() => laden())
        .catch((f) => setMeldung(f.message))
    },
    [laden],
  )

  const loeschen = useCallback(
    (id) => {
      setNachfrage(null)
      api
        .del('/api/cards', { id })
        .then(() => laden())
        .catch((f) => setMeldung(f.message))
    },
    [laden],
  )

  /**
   * Lernstand einer Karte zurücksetzen. Löscht die Karte nicht und rührt die
   * Bewertungshistorie nicht an — die Karte ist danach nur wieder fällig wie
   * eine neue. Das Ergebnis steht sofort in der Meta-Zeile („neu"), deshalb
   * braucht es keine Erfolgsmeldung.
   */
  const zuruecksetzen = useCallback(
    (id) => {
      setNachfrage(null)
      setMeldung(null)
      api
        .post('/api/cards/reset-state', { id })
        .then(() => laden())
        .catch((f) => setMeldung(f.message))
    },
    [laden],
  )

  const nachfragen = useCallback((id, aktion) => setNachfrage({ id, aktion }), [])
  const abbrechen = useCallback(() => setNachfrage(null), [])

  /** Verschiebt eine Karte um eine Stelle und schickt die ganze Ordnerfolge. */
  const schieben = useCallback(
    (id, richtung) => {
      const karten = daten?.cards ?? []
      const von = karten.findIndex((k) => k.id === id)
      const nach = von + richtung
      if (von < 0 || nach < 0 || nach >= karten.length) return

      const neu = [...karten]
      ;[neu[von], neu[nach]] = [neu[nach], neu[von]]
      setDaten({ ...daten, cards: neu })

      api
        .post('/api/cards/reorder', { ids: neu.map((k) => k.id) })
        .then(() => laden())
        .catch((f) => setMeldung(f.message))
    },
    [daten, laden],
  )

  const reihenfolgeZuruecksetzen = useCallback(() => {
    api
      .post('/api/cards/reset-order', { folder: prefix || null })
      .then(() => laden())
      .catch((f) => setMeldung(f.message))
  }, [prefix, laden])

  /**
   * Thema archivieren oder zurückholen. Steht hier und nicht in der
   * Themenliste: die Startseite bleibt eine reine Liste, das Umschalten ist
   * Verwaltung (DESIGN.md, Dichte „komfortabel").
   */
  const archivUmschalten = useCallback(
    (archiviert) => {
      setMeldung(null)
      api
        .post('/api/topics/archive', { folder: prefix, archived: archiviert })
        .then(() => laden())
        .catch((f) => setMeldung(f.message))
    },
    [prefix, laden],
  )

  /**
   * Thema in beide Richtungen abfragen — oder wieder nur vorwärts.
   *
   * Steht neben dem Archiv-Schalter und aus demselben Grund: die Startseite
   * bleibt eine reine Liste, das Umschalten ist Verwaltung.
   */
  const umkehrUmschalten = useCallback(
    (beidseitig) => {
      setMeldung(null)
      api
        .post('/api/topics/umkehr', { folder: prefix, umkehr: beidseitig })
        .then(() => laden())
        .catch((f) => setMeldung(f.message))
    },
    [prefix, laden],
  )

  const waisenAufraeumen = useCallback(() => {
    api
      .post('/api/cards/prune-orphans', {})
      .then(() => laden())
      .catch((f) => setMeldung(f.message))
  }, [laden])

  if (laedt && !daten) return <Laden />
  if (fehler) return <Fehler fehler={fehler} erneut={laden} />

  const karten = daten?.cards ?? []
  const ordner = daten?.folders ?? []
  const archiv = daten?.archiv ?? []
  const umkehr = daten?.umkehr ?? []
  const imArchiv = Boolean(prefix) && archiv.includes(prefix)
  const beidseitig = Boolean(prefix) && umkehr.includes(prefix)
  const heute = heuteIso()

  return (
    <Seite titel="Karten">
      <div class="karten__kopf">
        <label class="feld karten__suche">
          <span class="feld__label meta">Suche</span>
          <input
            class="feld__eingabe"
            type="search"
            value={frage}
            onInput={(e) => setFrage(e.currentTarget.value)}
            placeholder="Vorderseiten durchsuchen"
          />
        </label>

        <label class="feld karten__filter">
          <span class="feld__label meta">Thema</span>
          <select
            class="feld__eingabe"
            value={prefix}
            onChange={(e) => setPrefix(e.currentTarget.value)}
          >
            <option value="">Alle Themen</option>
            {ordner.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </label>

        <a class="knopf knopf--haupt" href={`/karten/neu${prefix ? `?folder=${encodeURIComponent(prefix)}` : ''}`}>
          Neue Karte
        </a>

        <a class="knopf knopf--still" href={`/karten/bild${prefix ? `?folder=${encodeURIComponent(prefix)}` : ''}`}>
          Neue Bildkarten
        </a>
      </div>

      {prefix ? (
        <section class="themenschalter-block" aria-label={`Einstellungen für ${prefix}`}>
          <Themenschalter
            titel="Archiviert"
            an={imArchiv}
            aufAendern={archivUmschalten}
            hinweis="Zählt nicht zu „Alle lernen“, bleibt aber einzeln lernbar. Gelöscht wird nichts."
          />
          <Themenschalter
            titel="Beide Richtungen"
            an={beidseitig}
            aufAendern={umkehrUmschalten}
            hinweis="Jede Karte kommt auch rückwärts dran, mit eigenem Lernstand. Multiple-Choice- und Bildkarten bleiben einseitig."
          />
        </section>
      ) : null}

      <p class="meta karten__zahl">
        {karten.length} {karten.length === 1 ? 'Karte' : 'Karten'}
        {daten?.due_count ? ` · ${daten.due_count} fällig` : null}
      </p>

      {meldung ? (
        <p class="zustand zustand--fehler" role="alert">
          {meldung}
        </p>
      ) : null}

      {karten.length ? (
        <ul class="kartenliste">
          {karten.map((karte, i) => (
            <Kartenzeile
              key={karte.id}
              karte={karte}
              heute={heute}
              ordner={ordner}
              istErste={i === 0}
              istLetzte={i === karten.length - 1}
              offen={offen === karte.id}
              aufUmschalten={umschalten}
              nachfrage={nachfrage?.id === karte.id ? nachfrage.aktion : null}
              aufVerschieben={verschieben}
              aufLoeschen={loeschen}
              aufZuruecksetzen={zuruecksetzen}
              aufSchieben={schieben}
              aufNachfrage={nachfragen}
              aufAbbrechen={abbrechen}
            />
          ))}
        </ul>
      ) : (
        <Leer text="Keine Karte passt zu dieser Suche." />
      )}

      <div class="karten__fuss">
        <button type="button" class="knopf knopf--still" onClick={reihenfolgeZuruecksetzen}>
          Reihenfolge zurücksetzen
        </button>      </div>

      {daten?.orphans?.length ? (
        <section class="waisen">
          <h2 class="waisen__titel">Lernstände ohne Datei</h2>
          <p class="meta">
            Diese Einträge zeigen auf Karten, die es nicht mehr gibt. Sie stören nichts, lassen
            sich aber entfernen.
          </p>
          <ul class="waisen__liste meta">
            {daten.orphans.map((id) => (
              <li key={id}>{id}</li>
            ))}
          </ul>
          <button type="button" class="knopf knopf--still" onClick={waisenAufraeumen}>
            Aufräumen
          </button>
        </section>
      ) : null}
    </Seite>
  )
}
